import { useEffect, useState } from "react";
import { Brain, Megaphone, Briefcase, Settings2, BarChart3, TrendingUp, CalendarCheck, Mail } from "lucide-react";
import { PortalPage, Card } from "@/components/PortalPage";
import { Accordion, Bullets, Tabs, useSingleOpen } from "@/components/ContentParts";
import { api } from "@/lib/api";
import guides from "@/content/guides.json";

const { pillars, weekly, monthlyNonNegotiables, truths, contacts } = guides.franchise;
const ICONS = [Brain, Megaphone, Briefcase, Settings2, BarChart3, TrendingUp];
const TABS = [...pillars.map((p) => ({ key: String(p.id), label: p.label })), { key: "weekly", label: "WEEKLY RHYTHM" }, { key: "truths", label: "NO EXCUSES" }];

const Pillar = ({ p }) => {
  const [openId, toggle] = useSingleOpen();
  const Icon = ICONS[p.id - 1] || Brain;
  return (
    <>
      <Card className="p-6" style={{ borderColor: "var(--accent)" }} data-testid={`pillar-${p.id}`}>
        <Icon size={28} className="text-[var(--accent)]" />
        <p className="mt-3 text-[10px] font-bold tracking-[0.3em] text-[var(--accent)]">PILLAR {p.id} · {p.label}</p>
        <h2 className="mt-1 font-display text-3xl font-extrabold uppercase leading-tight">{p.title}</h2>
        <p className="mt-3 text-[15px] leading-7 text-neutral-300">{p.intro}</p>
      </Card>
      {p.sections.map((s, i) => (
        <Accordion key={i} id={`${p.id}-${i}`} title={s.heading} open={openId === i} onToggle={() => toggle(i)}>
          {s.body && <p>{s.body}</p>}
          {s.bullets && <Bullets items={s.bullets} />}
        </Accordion>
      ))}
    </>
  );
};

const Weekly = () => (
  <>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="weekly-rhythm">
      {weekly.map((d) => (
        <Card key={d.day} className="p-4">
          <p className="font-display text-2xl font-extrabold text-[var(--accent)]">{d.day}</p>
          <ul className="mt-2 space-y-2 text-sm text-neutral-300">{d.tasks.map((t) => <li key={t} className="flex gap-2"><CalendarCheck size={14} className="mt-1 shrink-0 text-neutral-500" />{t}</li>)}</ul>
        </Card>
      ))}
    </div>
    <Card className="p-6" data-testid="monthly-non-negotiables">
      <p className="font-display text-xl font-bold uppercase">Monthly non-negotiables</p>
      <div className="text-[15px] leading-7 text-neutral-300"><Bullets items={monthlyNonNegotiables} /></div>
    </Card>
  </>
);

const Truths = () => (
  <div className="grid gap-3 sm:grid-cols-2" data-testid="no-excuses">
    {truths.map((t) => (
      <Card key={t.myth} className="p-5">
        <p className="text-sm italic text-[#FF6B6B] line-through decoration-[#FF6B6B]/60">{t.myth}</p>
        <p className="mt-2 text-[15px] leading-7 text-neutral-200">{t.truth}</p>
      </Card>
    ))}
  </div>
);

export default function FranchiseSuccessGuide() {
  const [tab, setTab] = useState("1");
  useEffect(() => { api("/activity/track", { method: "POST", body: { key: "franchise_success_guide_viewed" } }).catch(() => {}); }, []);
  const pillar = pillars.find((p) => String(p.id) === tab);
  return (
    <PortalPage eyebrow="FRANCHISE SUCCESS GUIDE" title="Run it like a CEO" testId="franchise-success-guide-page" width="max-w-5xl">
      <p className="text-sm text-neutral-400">Six pillars every top ALR franchisee masters — plus the weekly rhythm that keeps the business growing.</p>
      <Tabs tabs={TABS} active={tab} onChange={(k) => { setTab(k); window.scrollTo(0, 0); }} />
      {pillar && <Pillar key={tab} p={pillar} />}
      {tab === "weekly" && <Weekly />}
      {tab === "truths" && <Truths />}
      <Card className="p-5" data-testid="guide-contacts">
        <p className="mb-2 text-[11px] font-bold tracking-[0.3em] text-neutral-400">KEY CONTACTS</p>
        {contacts.map(([k, v]) => <p key={k} className="flex flex-wrap gap-x-3 border-b border-[#2a2a2a] py-2 text-sm last:border-0"><Mail size={14} className="mt-1 text-[var(--accent)]" /><b className="min-w-[150px]">{k}</b><span className="text-neutral-400">{v}</span></p>)}
      </Card>
    </PortalPage>
  );
}
